/**
 * Challenges Hero Section
 * Gradient hero with level and stats
 */

import { Trophy, Zap, Flame, Share2 } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import type { ChallengesHeroStats } from '../types';
import { formatXp } from '../utils/helpers';

interface ChallengesHeroProps {
  stats: ChallengesHeroStats;
}

function StatCard({
  value,
  label,
  icon: Icon,
  iconBg,
}: {
  value: string | number;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  iconBg: string;
}) {
  return (
    <div className="flex items-center gap-3 p-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
      <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <div className="text-xl font-bold text-white">{value}</div>
        <div className="text-xs text-primary-100">{label}</div>
      </div>
    </div>
  );
}

export function ChallengesHero({ stats }: ChallengesHeroProps) {
  const { t } = useLanguage();

  return (
    <section className="relative min-h-[35vh] overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
      {/* Decorative blur circles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl opacity-10" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-accent rounded-full blur-3xl opacity-15" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium text-white mb-6 border border-white/20 animate-fade-in">
          <Trophy className="w-4 h-4" />
          {t('challenges.badge', 'Daily Challenges')}
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-white animate-slide-up">
          {t('challenges.heroTitle', 'Earn XP &')}{' '}
          <span className="text-accent">{t('challenges.heroTitleHighlight', 'Level Up')}</span>
        </h1>

        {/* Subtitle */}
        <p className="mt-4 text-lg text-primary-100 max-w-2xl animate-slide-up animation-delay-100">
          {t('challenges.heroSubtitle', 'Complete daily challenges to earn rewards and unlock badges!')}
        </p>

        {/* Stats row */}
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl animate-fade-in animation-delay-200">
          <StatCard
            value={`Lv. ${stats.currentLevel}`}
            label={t('challenges.stats.level', 'Current Level')}
            icon={Trophy}
            iconBg="bg-yellow-500/30"
          />
          <StatCard
            value={formatXp(stats.totalXp)}
            label={t('challenges.stats.xp', 'Total XP')}
            icon={Zap}
            iconBg="bg-purple-500/30"
          />
          <StatCard
            value={stats.dayStreak}
            label={t('challenges.stats.streak', 'Day Streak')}
            icon={Flame}
            iconBg="bg-orange-500/30"
          />
          <StatCard
            value={`${stats.sharesToday}/${stats.maxSharesPerDay}`}
            label={t('challenges.stats.shares', 'Shares Today')}
            icon={Share2}
            iconBg="bg-blue-500/30"
          />
        </div>
      </div>

      {/* Wave divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 80" fill="none" className="w-full h-auto">
          <path
            d="M0 80L60 70C120 60 240 40 360 35C480 30 600 40 720 45C840 50 960 50 1080 45C1200 40 1320 30 1380 25L1440 20V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0Z"
            fill="#f9fafb"
          />
        </svg>
      </div>
    </section>
  );
}
